<template>
  <div class="card card-custom">
    <div class="card-body container-fluid py-8 px-8 py-lg-10 px-lg-10">
      <WorkQueueChrome
        :active-lane="'maintenance'"
        :crumbs="crumbs"
        subtitle="Policy"
        @lane="goLane"
      />

      <b-alert v-if="errorAlert" variant="warning" show>
        <div class="text-dark">{{ errorAlert }}</div>
      </b-alert>

      <div v-if="loaded && card">
        <div class="mb-4">
          <a
            v-if="card.clientId"
            href="#"
            class="text-muted font-size-sm"
            @click.prevent="$router.push(`/workbench/clients/${card.clientId}`)"
          >
            ← {{ card.clientName || "Client" }}
          </a>
        </div>
        <h2 class="font-weight-bolder text-dark">{{ card.policy.policyNumber }}</h2>
        <div class="text-muted mb-6">
          {{ card.policy.brand }} · {{ card.policy.status }} ·
          {{ formatDate(card.policy.startDate) }} to {{ formatDate(card.policy.endDate) }}
        </div>

        <div class="mb-6">
          <button
            class="btn btn-light-primary font-weight-bold text-uppercase mr-2"
            @click.prevent="$router.push(`/wizard/policy-details-view?policyId=${card.policy.id}`)"
          >
            Open full policy view
          </button>
          <button
            class="btn btn-primary font-weight-bold text-uppercase mr-2"
            :disabled="busy"
            @click.prevent="showEndorsementModal = true"
          >
            Process endorsement
          </button>
          <button
            v-if="card.clientId"
            class="btn btn-light font-weight-bold text-uppercase"
            @click.prevent="$router.push(`/workbench/clients/${card.clientId}`)"
          >
            Open client
          </button>
        </div>

        <b-modal
          v-model="showEndorsementModal"
          title="Process endorsement"
          ok-title="Create"
          :ok-disabled="!canCreateEndorsement || busy"
          @ok.prevent="createEndorsement"
        >
          <p class="text-muted font-size-sm">
            Mid-term change to this bound Fuse policy. Set the effective date inside the period of
            insurance, then choose one type. This does not rate, Stamp, or Post.
          </p>
          <div class="form-group">
            <label class="font-weight-bold">Effective date</label>
            <input v-model="endorsementEffectiveDate" type="date" class="form-control" />
            <div class="text-muted font-size-sm mt-1">
              Period of insurance
              {{ formatDate(card.policy.startDate) }} to {{ formatDate(card.policy.endDate) }}.
            </div>
          </div>
          <div class="form-group mb-0">
            <label class="font-weight-bold">Type of transaction</label>
            <select v-model="endorsementKind" class="form-control">
              <option value="">Select…</option>
              <option value="add_vehicle">Add vehicle</option>
              <option value="remove_vehicle">Remove vehicle</option>
              <option value="update_details">Update details</option>
              <option value="cancel_policy">Cancel policy</option>
            </select>
          </div>
        </b-modal>

        <h4 class="font-weight-bold mb-3">Quotes</h4>
        <div v-if="!(card.quotes || []).length" class="text-muted">No quotes on this policy.</div>
        <div
          v-for="quote in card.quotes"
          :key="quote.id"
          class="d-flex justify-content-between align-items-center border rounded p-4 mb-3"
        >
          <div>
            <div class="font-weight-bold">{{ quote.name || "Quote" }}</div>
            <div class="text-muted font-size-sm">{{ quote.status }}</div>
          </div>
          <button class="btn btn-sm btn-primary font-weight-bold text-uppercase" :disabled="busy" @click.prevent="copyQuote(quote)">
            Copy into this year's renewal
          </button>
        </div>

        <h4 class="font-weight-bold mb-3 mt-8">Fleet</h4>
        <div v-if="!card.fleet.available" class="text-muted">{{ card.fleet.message }}</div>
        <div v-else class="text-dark">
          {{ card.fleet.vehicleCount }} vehicles
          <span v-if="card.fleet.mainTypes && card.fleet.mainTypes.length">
            · {{ card.fleet.mainTypes.join(", ") }}
          </span>
        </div>

        <div class="mt-8 border-top pt-4">
          <a href="#" class="text-muted font-size-sm" @click.prevent="showDetails = !showDetails">
            {{ showDetails ? "Hide record details" : "Record details" }}
          </a>
          <div v-if="showDetails" class="mt-3 font-size-sm text-muted">
            <div>CRM policy: {{ card.recordDetails.crmPolicyId }}</div>
            <div v-if="card.recordDetails.insightPolicyId">Billing policy: {{ card.recordDetails.insightPolicyId }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment-timezone";
import { ADD_BODY_CLASSNAME, REMOVE_BODY_CLASSNAME } from "@/core/services/store/htmlclass.module";
import WorkQueueChrome from "./components/WorkQueueChrome.vue";
import { getPolicyCard, copyPriorQuote, createPolicyEndorsement } from "@/services/work-queue-service";

export default {
  name: "PolicyCardPage",
  components: { WorkQueueChrome },
  data() {
    return {
      loaded: false,
      busy: false,
      errorAlert: null,
      card: null,
      showDetails: false,
      showEndorsementModal: false,
      endorsementEffectiveDate: "",
      endorsementKind: ""
    };
  },
  computed: {
    crumbs() {
      const crumbs = [];
      if (this.card && this.card.clientId) {
        crumbs.push({
          label: this.card.clientName || "Client",
          to: `/workbench/clients/${this.card.clientId}`
        });
      }
      crumbs.push({ label: (this.card && this.card.policy && this.card.policy.policyNumber) || "Policy" });
      return crumbs;
    },
    canCreateEndorsement() {
      return Boolean(this.endorsementEffectiveDate && this.endorsementKind);
    }
  },
  mounted() {
    this.$store.commit("setWizardTitle", "Underwriting Work Queue");
    this.$store.commit("setWizardSubtitle", "Policy");
    this.load();
  },
  methods: {
    formatDate(isoDate) {
      if (!isoDate) return "";
      return moment.tz(String(isoDate).slice(0, 10), "YYYY-MM-DD", "Australia/Sydney").format("DD/MM/YYYY");
    },
    goLane(lane) {
      this.$router.push({ path: "/workbench", query: { lane } }).catch(() => {});
    },
    async load() {
      this.$store.dispatch(ADD_BODY_CLASSNAME, "page-loading");
      try {
        this.card = await getPolicyCard(this.$route.params.policyId);
        this.loaded = true;
      } catch (_e) {
        this.errorAlert = "Unable to load this policy right now.";
      } finally {
        this.$store.dispatch(REMOVE_BODY_CLASSNAME, "page-loading");
      }
    },
    async copyQuote(quote) {
      this.busy = true;
      try {
        const result = await copyPriorQuote(quote.id, {
          target: "renewal",
          policyId: this.card.policy.id
        });
        if (result && result.error) {
          this.errorAlert = result.error;
          return;
        }
        if (result && result.transactionId) {
          this.$router
            .push(`/wizard/nb-rn?transactionId=${result.transactionId}&from=policy:${this.card.policy.id}`)
            .catch(() => {});
        }
      } catch (_e) {
        this.errorAlert = "Unable to copy the quote right now.";
      } finally {
        this.busy = false;
      }
    },
    async createEndorsement() {
      if (!this.canCreateEndorsement || this.busy) return;
      this.busy = true;
      this.errorAlert = null;
      try {
        const result = await createPolicyEndorsement(this.card.policy.id, {
          kind: this.endorsementKind,
          effectiveDate: this.endorsementEffectiveDate
        });
        if (result && result.error) {
          this.errorAlert = result.error;
          return;
        }
        if (result && result.unpostedWarning) {
          this.errorAlert = result.unpostedWarning;
        }
        this.showEndorsementModal = false;
        if (result && result.transactionId) {
          this.$router.push(`/workbench/endorsements/${result.transactionId}`).catch(() => {});
        }
      } catch (error) {
        const serverMessage = error && error.response && error.response.data && error.response.data.error;
        this.errorAlert = serverMessage || "Unable to create the endorsement work item right now.";
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>
